import { Badge, Card, Heading, IconButton, Input, LuIcon, Menu, Separator, Text, toast } from "@overmap-ai/blocks"
import {
	AssetStatusType,
	type AssetType,
	type AssetTypeStatus,
	OrganizationAccessLevel,
	type Stored,
	ASSET_STATUS_TYPES,
	ERROR_MSG_UNEXPECTED,
} from "@overmap-ai/common"
import { APIError, selectAssetTypeById, selectAssetTypeStatusesOfAssetType } from "@overmap-ai/core"
import { getRouteApi } from "@tanstack/react-router"
import { type FocusEventHandler, memo, useCallback, useMemo } from "react"

import { ColorButton } from "../../components/Buttons/ColorButton"
import { ViewContainer } from "../../components/Containers/ViewContainer"
import { CreateAssetTypeStatusDialog } from "../../components/Dialogs/CreateAssetTypeStatusDialog"
import { OrganizationAccessRequired } from "../../components/OrganizationAccessRequired/AccessRequired"
import { ColorPickerPopover } from "../../components/Popovers/ColorPickerPopover"
import { useUserOrganizationAccess } from "../../hooks/users"
import { useBlurInput, useConfirmDeleteDialog } from "../../hooks/utils"
import { assetStatusTypeMapping } from "../../mappings/assets"
import { modelIconMapping } from "../../mappings/icons"
import { sdk } from "../../sdk"
import { useAppSelector, useShallowEqaulSelector } from "../../store"

const routeApi = getRouteApi("/_loggedIn/$organizationId/_map/_layout/settings/asset-types/$assetTypeId/statuses")

export const AssetTypeSettingsStatusView = memo(() => {
	const { assetTypeId } = routeApi.useParams()
	const assetType = useAppSelector(selectAssetTypeById(assetTypeId))

	if (!assetType) {
		throw new Error(`expected assetType to exist, got ${assetType}.`)
	}

	const assetTypeStatuses = useShallowEqaulSelector(selectAssetTypeStatusesOfAssetType(assetType.uuid))

	const statusesByType = useMemo(() => {
		const grouped = new Map<AssetStatusType, Stored<AssetTypeStatus>[]>()

		ASSET_STATUS_TYPES.forEach((type) => grouped.set(type, []))

		assetTypeStatuses.forEach((status) => {
			grouped.get(status.type)?.push(status)
		})

		return grouped
	}, [assetTypeStatuses])

	const handleSubmit = useCallback(
		(values: Pick<AssetTypeStatus, "color" | "name" | "type">) => {
			const existingStatusesOfType = statusesByType.get(values.type) ?? []
			const nextOrder = existingStatusesOfType.length

			const [, promise] = sdk.assetTypeStatuses.add({
				asset_type: assetType.uuid,
				name: values.name,
				color: values.color,
				type: values.type,
				order: nextOrder,
			})

			promise.catch((error) => {
				toast.error({
					title: "Failed to create status",
					description: error instanceof APIError ? error.message : ERROR_MSG_UNEXPECTED,
				})
			})
		},
		[assetType.uuid, statusesByType],
	)

	return (
		<ViewContainer maxWidth="4xl">
			<section className="flex size-full flex-col gap-4">
				<div className="flex w-full flex-col gap-1">
					<Heading size="xl">Statuses</Heading>
					<Text size="sm" accentColor="base">
						Manage asset type statuses.
					</Text>
				</div>

				<Separator size="full" />

				<Card className="flex flex-col gap-4" size="sm">
					{ASSET_STATUS_TYPES.map((statusType) => {
						const statuses = statusesByType.get(statusType) ?? []
						const { color, label } = assetStatusTypeMapping[statusType]

						return (
							<div key={statusType} className="flex flex-col gap-4">
								<Card
									variant="soft"
									className="px-4! flex items-center justify-between gap-2"
									size="xs"
								>
									<Text accentColor="base">{label}</Text>
									<OrganizationAccessRequired accessLevel={OrganizationAccessLevel.ADMIN} type="hide">
										<CreateAssetTypeStatusDialog
											onSubmit={handleSubmit}
											defaultColor={color}
											statusType={statusType}
											size="sm"
										>
											<IconButton variant="ghost" size="sm" accentColor="base">
												<LuIcon icon={modelIconMapping.add} />
											</IconButton>
										</CreateAssetTypeStatusDialog>
									</OrganizationAccessRequired>
								</Card>

								{statuses.length > 0 && (
									<ol className="flex flex-col gap-4">
										{statuses.map((assetTypeStatus) => (
											<AssetTypeStatusListItem
												key={assetTypeStatus.uuid}
												assetTypeStatus={assetTypeStatus}
												assetType={assetType}
											/>
										))}
									</ol>
								)}
							</div>
						)
					})}
				</Card>
			</section>
		</ViewContainer>
	)
})
AssetTypeSettingsStatusView.displayName = "AssetTypeSettingsStatusView"

interface AssetTypeStatusListItemProps {
	assetTypeStatus: Stored<AssetTypeStatus>
	assetType: Stored<AssetType>
}

const AssetTypeStatusListItem = memo<AssetTypeStatusListItemProps>((props) => {
	const { assetTypeStatus, assetType } = props

	const openConfirmDeleteDialog = useConfirmDeleteDialog()
	const isAdmin = useUserOrganizationAccess(OrganizationAccessLevel.ADMIN)
	const isDefault = assetType.default_status === assetTypeStatus.uuid
	const typeData = assetStatusTypeMapping[assetTypeStatus.type]

	const handleNameInputBlur = useCallback<FocusEventHandler<HTMLInputElement>>(
		(e) => {
			const value = e.target.value

			if (assetTypeStatus.name === value) return

			const [, promise] = sdk.assetTypeStatuses.update({
				uuid: assetTypeStatus.uuid,
				name: e.target.value,
			})

			promise.catch((error) => {
				toast.error({
					title: "Failed to update status",
					description: error instanceof APIError ? error.message : ERROR_MSG_UNEXPECTED,
				})
			})
		},
		[assetTypeStatus.uuid, assetTypeStatus.name],
	)

	const nameInputProps = useBlurInput<HTMLInputElement>(assetTypeStatus.name, handleNameInputBlur, true)

	const handleColorChange = useCallback(
		(color: string) => {
			const [, promise] = sdk.assetTypeStatuses.update({
				uuid: assetTypeStatus.uuid,
				color: color,
			})

			promise.catch((error) => {
				toast.error({
					title: "Failed to update status color",
					description: error instanceof APIError ? error.message : ERROR_MSG_UNEXPECTED,
				})
			})
		},
		[assetTypeStatus.uuid],
	)

	const handleSetDefault = useCallback(() => {
		const [, promise] = sdk.assetTypes.update({
			uuid: assetType.uuid,
			default_status: assetTypeStatus.uuid,
		})

		promise.catch((error) => {
			toast.error({
				title: "Failed to set default status",
				description: error instanceof APIError ? error.message : ERROR_MSG_UNEXPECTED,
			})
		})
	}, [assetType.uuid, assetTypeStatus.uuid])

	const handleDelete = useCallback(() => {
		openConfirmDeleteDialog({
			title: "Delete status",
			description: "The status will be deleted immediately. This action cannot be undone.",
			onAction: () =>
				sdk.assetTypeStatuses.delete(assetTypeStatus.uuid).catch((error) => {
					toast.error({
						title: "Failed to delete status",
						description: error instanceof APIError ? error.message : ERROR_MSG_UNEXPECTED,
					})
				}),
		})
	}, [assetTypeStatus.uuid, openConfirmDeleteDialog])

	return (
		<li className="flex items-center justify-between gap-2">
			<div className="flex grow items-center gap-2">
				<ColorPickerPopover required color={assetTypeStatus.color} onColorChange={handleColorChange} size="sm">
					<ColorButton disabled={!isAdmin} color={`${assetTypeStatus.color}40`}>
						<LuIcon icon={typeData.icon} color={assetTypeStatus.color} />
					</ColorButton>
				</ColorPickerPopover>

				<Input.Root variant="outline" className="grow">
					<Input.Field placeholder="Enter a name" disabled={!isAdmin} {...nameInputProps} />
				</Input.Root>
			</div>

			<div className="flex items-center gap-1">
				{isDefault && (
					<Badge variant="soft" size="sm" accentColor="base">
						Default
					</Badge>
				)}

				<OrganizationAccessRequired accessLevel={OrganizationAccessLevel.ADMIN} type="hide">
					<Menu.Root align="end">
						<Menu.ClickTrigger>
							<IconButton accentColor="base" variant="ghost" size="sm">
								<LuIcon icon={modelIconMapping.more} />
							</IconButton>
						</Menu.ClickTrigger>
						<Menu.Content size="sm">
							<Menu.Item onSelect={handleSetDefault}>
								<LuIcon icon={modelIconMapping.copy} />
								Make default
							</Menu.Item>
							<Menu.Separator />
							<Menu.Item onSelect={handleDelete}>
								<LuIcon icon={modelIconMapping.delete} /> Delete
							</Menu.Item>
						</Menu.Content>
					</Menu.Root>
				</OrganizationAccessRequired>
			</div>
		</li>
	)
})
AssetTypeStatusListItem.displayName = "AssetTypeStatusListItem"
